import { CanActivateFn, Router } from "@angular/router";
import { inject } from "@angular/core";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { InteractionStatus } from "@azure/msal-browser";
import { AuthorizationService } from "../services/application.service";
import { DatexErrorService } from "wavelength-ui";
import { filter, firstValueFrom } from "rxjs";

export const canViewManagement: CanActivateFn =
  async () => {
    const authService = inject(AuthorizationService);
    const router = inject(Router);
    const datexErrorService = inject(DatexErrorService);
    const msalService = inject(MsalService);
    const msalBroadcastService = inject(MsalBroadcastService);

    // Angular runs this route's guards in parallel, so this one starts while MsalGuard may
    // still be signing the person in or processing the redirect they came back on. Asking
    // the API about their access during that window sends a request with no token, and the
    // HTTP interceptor answers it with a popup - one that cannot carry whatever sign-in
    // hints the URL supplied. Waiting for MSAL to go idle first makes the account list
    // below trustworthy, whether this is a first visit or the return trip from Entra.
    await firstValueFrom(msalBroadcastService.inProgress$.pipe(
      filter(status => status === InteractionStatus.None)));

    // Still nobody signed in, so MsalGuard is already sending the browser to Entra and has
    // recorded this route as the one to come back to. Refusing here just stops this
    // navigation from proceeding in the meantime; it does not decide where we land.
    if (msalService.instance.getAllAccounts().length === 0) {
      return false;
    }

    const hasManagementAccess = await firstValueFrom(authService.hasManagementAccess());
    if (!hasManagementAccess) {
      const errorMesssage = `You are not authorized to access manager, please consult with your system admin.`;
      datexErrorService.add(new Error(errorMesssage));
      return router.createUrlTree(["/", "error"]);
    }

    return true;
  }
